import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1780130487332-b6a96a31b024?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxjaGlsZHJlbiUyMGxlYXJuaW5nJTIwbmF0dXJlJTIwc3VubGlnaHR8ZW58MHx8fHwxNzg0MjI4ODY2fDA&ixlib=rb-4.1.0&q=85";

/** Masked line-by-line reveal helper */
const line = {
  initial: { y: "110%" },
  animate: (i) => ({
    y: "0%",
    transition: { delay: 0.55 + i * 0.08, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] pt-24 md:pt-28 pb-16 md:pb-20 flex flex-col overflow-hidden paper-bg"
      data-testid="hero-section"
    >
      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-10 flex-1 grid grid-cols-12 gap-6 md:gap-10 items-center">
        {/* LEFT — title, paragraph, CTAs */}
        <div className="col-span-12 lg:col-span-7 xl:col-span-8">
          <h1
            className="font-sanskrit text-[11.5vw] md:text-[8vw] lg:text-[6.2vw] xl:text-[5.6vw] leading-[0.98] tracking-[-0.005em] text-[color:var(--ink)]"
            data-testid="hero-title"
          >
            <span className="reveal-mask">
              <motion.span className="reveal-line" custom={0} variants={line} initial="initial" animate="animate">
                Path of
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span className="reveal-line" custom={1} variants={line} initial="initial" animate="animate">
                Awakened
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span
                className="reveal-line flex items-baseline gap-3 md:gap-5"
                custom={2}
                variants={line}
                initial="initial"
                animate="animate"
              >
                <span>Intelligence.</span>
                <span
                  className="hidden md:inline-block h-[0.08em] flex-1 bg-[color:var(--ink)]/55 relative -top-[0.16em]"
                  aria-hidden
                />
              </motion.span>
            </span>
          </h1>

          {/* Paragraph — directly under title */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            data-testid="hero-sub"
            className="mt-6 md:mt-8 max-w-[58ch] font-serif-editorial text-[color:var(--ink)]/85 text-base md:text-lg lg:text-[19px] leading-[1.5]"
          >
            <p>
              Children are drowning in information and starving for insight. Most education points them outward — toward grades, approval, the next scroll.{" "}
              <span className="italic">shemushi</span> points them inward, toward clarity.
            </p>
            <p className="mt-3 md:mt-4">
              Insight isn&rsquo;t taught. It&rsquo;s uncovered — through{" "}
              <em className="italic">Inner Compass</em>, our live, in-person program for ages 5–18.
              Slowly, children uncover who they are.
            </p>
            <p className="mt-3 md:mt-4">
              What emerges is confidence. Sincerity. A self unshaken by what the world says —
              because it was never borrowed in the first place.
            </p>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            className="mt-7 md:mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a
              href="#enroll"
              className="group inline-flex items-center justify-between h-12 md:h-[52px] px-5 rounded-full bg-[color:var(--ink)] text-[color:var(--inv)] hover:bg-[color:var(--brand)] transition-colors duration-500 min-w-[220px]"
              data-testid="hero-cta-enroll"
            >
              <span className="text-sm tracking-wide">Enroll — Inner Compass</span>
              <span className="ml-4 transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#model"
              className="inline-flex items-center justify-between h-12 md:h-[52px] px-5 rounded-full border hairline text-[color:var(--ink)] hover:bg-[color:var(--ink)] hover:text-[color:var(--inv)] transition-colors duration-500 min-w-[220px]"
              data-testid="hero-cta-model"
            >
              <span className="text-sm tracking-wide">Read the SPI-PSY Model</span>
              <span className="ml-4">↓</span>
            </a>
          </motion.div>
        </div>

        {/* RIGHT — portrait */}
        <div className="col-span-12 lg:col-span-5 xl:col-span-4 mt-8 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1.1 }}
            className="relative max-w-[380px] ml-auto"
          >
            <div className="relative overflow-hidden rounded-t-[9999px] border hairline aspect-[3/4] spotlight">
              <motion.img
                src={HERO_IMG}
                alt="A child looking into a book"
                style={{ y: imgY, scale: imgScale }}
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom kinetic ticker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 1 }}
        className="max-w-[1440px] mx-auto w-full px-6 md:px-10 mt-4 flex items-center justify-between text-[10px] font-mono-editorial text-[color:var(--muted)]"
      >
        <span>Scroll to enter</span>
        <span className="hidden md:inline">शेमुषी · An Enrichment Studio</span>
        <span className="w-24 h-px bg-[color:var(--ink)]/40 relative overflow-hidden">
          <motion.span
            className="absolute inset-y-0 left-0 w-1/2 bg-[color:var(--ink)]"
            animate={{ x: ["-100%", "200%"] }}
            transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
}
